/**
 * COMP CLASS EVALUATIONS: the date, the request, the week pill. Pure, no
 * imports, so the server lane (netlify/functions/lib/compEvals.mjs) checks
 * input with the very same functions the screens draw with, and
 * tests/comp-evals.test.mjs runs this file straight under node.
 *
 * Bobbie, 2026-11-02: "all comp classes have to have an evaluation. it
 * should show up on the comp pages by the attendance. they should schedule
 * it with Clair or Kim and then there should be a tracker that all teachers
 * can see when evals have been completed and when. parents and dancers
 * should see the eval date in the class board and noted on the calendar as
 * a pill everywhere that week as dancers have to bring their costumes on
 * that day."
 *
 * WHAT ALREADY EXISTED, AND STAYS: the "done and complete" flag and the eval
 * notes on offeringStaffNotes/{offeringId} (DanceEvalCard on the class
 * About tab, DECISIONS 2026-09-01), and the Evals tab of the Recital/Comp
 * Tracker that reads them. This file adds only the DATE half:
 *
 *   compEvals/{offeringId}      staff only, NO client rule at all: the
 *                               request (who asked, who with, the day they
 *                               would like) and the confirmed date. Read and
 *                               written only by the compEvals server lane.
 *   compEvalDates/{offeringId}  the confirmed date and nothing else, so a
 *                               family may read it. Written only by the
 *                               lane; it exists only while a date is set.
 *
 * WHO DOES WHAT. A class's own teaching team (lead and co-teachers), a
 * Director or a Manager may ASK for an eval, naming the Director or Manager
 * they are scheduling it with. Only a Director or a Manager SETS the date
 * (Bobbie: "schedule it with Clair or Kim"). Done is still marked where it
 * always was.
 */

/** Where the browser asks the server lane. */
export const COMP_EVALS_ENDPOINT = '/.netlify/functions/compEvals';

/** What a dancer brings on eval day. One sentence, used on every surface. */
export const COMP_EVAL_COSTUME_LINE = 'Bring your costume.';

/** The title the eval carries on the class board and on calendars. */
export const COMP_EVAL_TITLE = 'Eval day';

export const COMP_EVAL_NOTE_MAX = 500;
export const COMP_EVAL_NAME_MAX = 120;

/** Roles that may set an eval date, and who a teacher schedules it with. */
export const COMP_EVAL_SCHEDULER_ROLES: readonly string[] = ['director', 'manager'];

/**
 * Roles that read the whole tracker: the Recital/Comp Tracker's own
 * audience (canSeeProductionProgress). Jr Staff are not in it, as there.
 */
export const COMP_EVAL_TRACKER_ROLES: readonly string[] = ['director', 'manager', 'company', 'foh'];

export type CompEvalStatus = 'requested' | 'scheduled';

export interface CompEvalRequest {
  byUid: string;
  byName: string;
  withUid: string;
  withName: string;
  /** 'YYYY-MM-DD' the teacher would like, or '' for "any day". */
  preferredDate: string;
  /** 'HH:MM' or ''. */
  preferredTime: string;
  note: string;
  atMs: number;
  /** The to-do made for the person it is scheduled with, when one was. */
  taskId: string;
  /**
   * The open eval time it was picked from (compEvalSlots/{slotId}), or ''
   * for a free-form ask. With a slot, preferredDate/preferredTime are the
   * slot's own day and start, and slotEndTime its end.
   */
  slotId: string;
  slotEndTime: string;
}

/** compEvals/{offeringId} as the lane returns it. */
export interface CompEvalRecord {
  offeringId: string;
  status: CompEvalStatus;
  request: CompEvalRequest | null;
  /** Set once status is 'scheduled'. */
  date: string;
  startTime: string;
  endTime: string;
  withUid: string;
  withName: string;
  scheduledByUid: string;
  scheduledByName: string;
  scheduledAtMs: number;
  updatedAtMs: number;
  /** The class board post that carries this date, so a move or a cancel can take it back. */
  boardPostId: string;
  /** The eval spots this date booked (compEvalSlots): every one the time covers. */
  slotIds: string[];
}

/** compEvalDates/{offeringId}: what a family may see. */
export interface CompEvalDate {
  offeringId: string;
  date: string;
  startTime: string;
  endTime: string;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** A real calendar day 'YYYY-MM-DD' (no Feb 30). */
export function isCompEvalDateKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1) return false;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= last;
}

export function isCompEvalTime(value: unknown): value is string {
  return typeof value === 'string' && TIME_RE.test(value);
}

function str(value: unknown, max: number): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function dateOr(value: unknown): string {
  return isCompEvalDateKey(value) ? value : '';
}

function timeOr(value: unknown): string {
  return isCompEvalTime(value) ? value : '';
}

/** Read a compEvals record defensively: anything malformed becomes empty. */
export function normalizeCompEvalRecord(offeringId: string, raw: unknown): CompEvalRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const status = data.status === 'scheduled' || data.status === 'requested' ? data.status : null;
  if (!status) return null;
  const req = data.request && typeof data.request === 'object' ? (data.request as Record<string, unknown>) : null;
  const request: CompEvalRequest | null = req
    ? {
        byUid: str(req.byUid, 128),
        byName: str(req.byName, COMP_EVAL_NAME_MAX),
        withUid: str(req.withUid, 128),
        withName: str(req.withName, COMP_EVAL_NAME_MAX),
        preferredDate: dateOr(req.preferredDate),
        preferredTime: timeOr(req.preferredTime),
        note: str(req.note, COMP_EVAL_NOTE_MAX),
        atMs: num(req.atMs),
        taskId: str(req.taskId, 128),
        slotId: str(req.slotId, 200),
        slotEndTime: timeOr(req.slotEndTime),
      }
    : null;
  const date = dateOr(data.date);
  // A "scheduled" record with no real date is not scheduled: say requested
  // (or nothing) rather than draw a date that is not there.
  if (status === 'scheduled' && !date) {
    return request ? { ...emptyRecord(offeringId), status: 'requested', request } : null;
  }
  return {
    offeringId,
    status,
    request,
    date: status === 'scheduled' ? date : '',
    startTime: status === 'scheduled' ? timeOr(data.startTime) : '',
    endTime: status === 'scheduled' ? timeOr(data.endTime) : '',
    withUid: str(data.withUid, 128),
    withName: str(data.withName, COMP_EVAL_NAME_MAX),
    scheduledByUid: str(data.scheduledByUid, 128),
    scheduledByName: str(data.scheduledByName, COMP_EVAL_NAME_MAX),
    scheduledAtMs: num(data.scheduledAtMs),
    updatedAtMs: num(data.updatedAtMs),
    boardPostId: status === 'scheduled' ? str(data.boardPostId, 128) : '',
    slotIds: status === 'scheduled' && Array.isArray(data.slotIds)
      ? data.slotIds.filter((id): id is string => typeof id === 'string' && id.length > 0 && id.length <= 200).slice(0, COMP_EVAL_COVER_MAX)
      : [],
  };
}

function emptyRecord(offeringId: string): CompEvalRecord {
  return {
    offeringId,
    status: 'requested',
    request: null,
    date: '',
    startTime: '',
    endTime: '',
    withUid: '',
    withName: '',
    scheduledByUid: '',
    scheduledByName: '',
    scheduledAtMs: 0,
    updatedAtMs: 0,
    boardPostId: '',
    slotIds: [],
  };
}

/**
 * Is this offering a comp class, the classes that must have an eval. Mirrors
 * isCompetitionOffering in ./offerings.ts on the raw document, so the lane
 * can refuse an eval date on a recreational class (no imports, so the
 * server can load this file). Keep the two in step.
 */
export function isCompEvalOffering(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const data = raw as Record<string, unknown>;
  const kind = data.kind;
  if (kind === 'comp-dance' || kind === 'comp-class') return true;
  if (kind === 'class' || kind === 'workshop') return false;
  const style = typeof data.style === 'string' ? data.style : '';
  const name = typeof data.name === 'string' ? data.name.toLowerCase() : '';
  return (
    style === 'Combo Contemporary/Commercial'
    || style === 'Combo Comp/Commercial'
    || ['comp', 'competition', 'company', 'team', 'solo', 'duet', 'trio'].some((word) => name.includes(word))
  );
}

/** Read a compEvalDates doc defensively. Null when it carries no real date. */
export function normalizeCompEvalDate(offeringId: string, raw: unknown): CompEvalDate | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const date = dateOr(data.date);
  if (!date) return null;
  const startTime = timeOr(data.startTime);
  const endTime = startTime ? timeOr(data.endTime) : '';
  return { offeringId, date, startTime, endTime: endTime && endTime > startTime ? endTime : '' };
}

// ---------------------------------------------------------------------------
// Calendar math. Day keys are plain 'YYYY-MM-DD' studio days; all arithmetic
// is UTC calendar arithmetic, so no device time zone can shift a day.
// ---------------------------------------------------------------------------

function keyToUtc(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function utcToKey(ms: number): string {
  const day = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}`;
}

/**
 * The Monday-to-Sunday week an eval falls in, as its first and last day
 * keys. "A pill everywhere that week": every surface asks this one question.
 */
export function compEvalWeek(dateKey: string): { start: string; end: string } | null {
  if (!isCompEvalDateKey(dateKey)) return null;
  const ms = keyToUtc(dateKey);
  const weekday = new Date(ms).getUTCDay(); // 0 Sunday .. 6 Saturday
  const back = (weekday + 6) % 7; // days since Monday
  const start = ms - back * 86_400_000;
  return { start: utcToKey(start), end: utcToKey(start + 6 * 86_400_000) };
}

/** True when `dayKey` is in the same Monday-to-Sunday week as the eval. */
export function isInCompEvalWeek(evalDate: string, dayKey: string): boolean {
  const week = compEvalWeek(evalDate);
  if (!week || !isCompEvalDateKey(dayKey)) return false;
  return dayKey >= week.start && dayKey <= week.end;
}

/** "6:30pm" from 'HH:MM'. */
export function compEvalClock(time: string): string {
  if (!isCompEvalTime(time)) return '';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Tue, Nov 10" from a day key. */
export function compEvalDayLabel(dateKey: string): string {
  if (!isCompEvalDateKey(dateKey)) return '';
  const day = new Date(keyToUtc(dateKey));
  return `${WEEKDAYS[day.getUTCDay()]}, ${MONTHS[day.getUTCMonth()]} ${day.getUTCDate()}`;
}

/** "Tue, Nov 10 at 6:30pm" (or without the time). */
export function compEvalWhenLabel(entry: { date: string; startTime?: string; endTime?: string }): string {
  const day = compEvalDayLabel(entry.date);
  if (!day) return '';
  const start = compEvalClock(entry.startTime ?? '');
  if (!start) return day;
  const end = compEvalClock(entry.endTime ?? '');
  return end ? `${day}, ${start} to ${end}` : `${day} at ${start}`;
}

/**
 * The pill's words for one day. Null when the day is outside the eval's
 * week, or the eval is already past: a pill about last week is noise.
 *
 * `todayKey` keeps a pill off once the eval day is behind us, even inside
 * the same week (Friday no longer needs Tuesday's reminder).
 */
export function compEvalPillText(
  entry: { date: string; startTime?: string },
  dayKey: string,
  todayKey: string,
): string | null {
  if (!isCompEvalDateKey(entry.date) || !isCompEvalDateKey(dayKey)) return null;
  if (isCompEvalDateKey(todayKey) && entry.date < todayKey) return null;
  if (!isInCompEvalWeek(entry.date, dayKey)) return null;
  const clock = compEvalClock(entry.startTime ?? '');
  if (entry.date === dayKey) {
    return `${COMP_EVAL_TITLE}${clock ? ` ${clock}` : ''}: ${COMP_EVAL_COSTUME_LINE.replace(/\.$/, '')}`;
  }
  const day = new Date(keyToUtc(entry.date));
  return `Eval ${WEEKDAYS[day.getUTCDay()]}${clock ? ` ${clock}` : ''}: ${COMP_EVAL_COSTUME_LINE.replace(/\.$/, '')}`;
}

/** The class board post a confirmed date sends to the class. */
export function compEvalClassPostBody(className: string, entry: { date: string; startTime?: string; endTime?: string }): string {
  const when = compEvalWhenLabel(entry);
  const name = className.trim() || 'this class';
  return `Eval day for ${name} is ${when}. Dancers, ${COMP_EVAL_COSTUME_LINE.charAt(0).toLowerCase()}${COMP_EVAL_COSTUME_LINE.slice(1)}`;
}

/** Tracker row state for one comp class. */
export type CompEvalTrackerState = 'done' | 'scheduled' | 'requested' | 'needs';

export function compEvalTrackerState(
  record: CompEvalRecord | null | undefined,
  evalComplete: boolean,
): CompEvalTrackerState {
  if (evalComplete) return 'done';
  if (record?.status === 'scheduled' && record.date) return 'scheduled';
  if (record?.status === 'requested') return 'requested';
  return 'needs';
}

export const COMP_EVAL_TRACKER_LABELS: Record<CompEvalTrackerState, string> = {
  done: 'Done',
  scheduled: 'Scheduled',
  requested: 'Asked, no date yet',
  needs: 'Needs scheduling',
};

/**
 * Which comp classes' eval pills a staff home shows (StaffCompEvalWeekPills).
 * A Director or Manager sees every class with a date. Anyone else sees the
 * classes where ANY of her own sign-ins teaches or assists, and the classes
 * she dances in: the weekly set the dancer portal hands a Jr Staff's dancer
 * sign-in (blendJrStaffClasses; a class she only covers as a one-week sub is
 * the one known difference), so she is one person on either sign-in
 * (final review on #1506: an assisted class was missing).
 */
export function staffCompEvalOfferingIds(input: {
  rows: readonly { id: string; teacherUids: readonly string[]; assistantUids: readonly string[] }[];
  datedIds: ReadonlySet<string> | ReadonlyMap<string, unknown>;
  ownUids: readonly string[];
  dancesIn: ReadonlySet<string>;
  seesAll: boolean;
}): string[] {
  const own = new Set(input.ownUids.filter(Boolean));
  const out: string[] = [];
  for (const row of input.rows) {
    if (!input.datedIds.has(row.id)) continue;
    const staffs = row.teacherUids.some((uid) => own.has(uid)) || row.assistantUids.some((uid) => own.has(uid));
    if (input.seesAll || staffs || input.dancesIn.has(row.id)) out.push(row.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// EVAL TIMES (Bobbie, 2026-11-03): "can you add a place they can upload a
// schedule? so Clair and Kim can add when they are available and people can
// select times from there", then "once Kim and Clair approve the time then
// the kids and parents get notified via message, and calendar when it is so
// they know when to bring their costumes", and "everyone needs to know how
// that works".
//
//   compEvalSlots/{slotId}   one open eval time a Director or Manager posted.
//                            Server lane only, no client rule at all (the
//                            compEvals posture). Listed to the tracker's
//                            audience so a class's teacher can pick one.
//
// THE FLOW. Ms Kim or Ms Clair posts open times (one at a time, or pastes a
// schedule; a long window may be cut into blocks). A comp class's teacher
// picks one on the class page's Eval card: the time is held for that class
// (no other class can pick it) and the person it belongs to gets a to-do.
// She approves it in one tap, which is the existing "Set the date": the date
// goes to compEvalDates (calendar entry and the week's costume pill for
// families and dancers) and to the class board (the message, with the class
// alert). Taking back the ask, clearing the date or moving it to another
// time hands the time back.
// ---------------------------------------------------------------------------

/** The page where Directors and Managers post their open eval times. */
export const COMP_EVAL_TIMES_PATH = '/eval-times';

/** Every eval spot is 10 minutes (Bobbie, 2026-11-03: "all spots should be 10mins"). */
export const COMP_EVAL_SLOT_MINUTES = 10;

/** How many spots one save may add: a whole pasted week or sheet. */
export const COMP_EVAL_SLOT_ADD_MAX = 200;

/** The longest window one line or row may name before it is cut into spots. */
export const COMP_EVAL_WINDOW_MAX_MINUTES = 12 * 60;

export type CompEvalSlotStatus = 'open' | 'asked' | 'booked';

export interface CompEvalSlotTime {
  date: string;
  startTime: string;
  endTime: string;
}

export interface CompEvalSlot extends CompEvalSlotTime {
  slotId: string;
  ownerUid: string;
  ownerName: string;
  status: CompEvalSlotStatus;
  /** The class it is held for ('' while open). */
  heldByOfferingId: string;
  heldByClassName: string;
  createdAtMs: number;
}

/** Every step, in plain words, shown on each eval screen. Everyone reads the same four lines. */
export const COMP_EVAL_HOW_IT_WORKS: readonly string[] = [
  'Ms Kim and Ms Clair post the times they are free for evals on the Eval times page.',
  'Each comp class\'s teacher picks one of those times on the class page (Eval card, under Take attendance).',
  'Ms Kim or Ms Clair approves it. Nothing goes to families until then.',
  'Once it is approved, parents and dancers get a message on the class board and the eval goes on their calendar, with a "Bring your costume" reminder all that week.',
];

/** The one-time announcement a Director sends from the Eval times page. */
export const COMP_EVAL_ANNOUNCEMENT_TITLE = 'How comp evals get scheduled';
export const COMP_EVAL_ANNOUNCEMENT_BODY = [
  'Every comp class has an eval. Here is how the day gets set:',
  ...COMP_EVAL_HOW_IT_WORKS.map((line, index) => `${index + 1}. ${line}`),
  'Parents and dancers: watch your class board and calendar. On eval day, bring your costume.',
].join('\n');

/** The to-do each Director and Manager gets, pointing at the page. */
export const COMP_EVAL_TIMES_TODO_TITLE = 'Add your eval times';
export const COMP_EVAL_TIMES_TODO_BODY =
  'Post the times you are free for comp evals on the Eval times page (menu: Eval times, or /eval-times).'
  + ' You can add them one at a time or paste your schedule. Teachers pick from your times,'
  + ' and you approve each one on the class page before families hear about it.';

/** One slot's id: the owner, the day and the start, so the same time twice is one doc. */
export function compEvalSlotId(ownerUid: string, date: string, startTime: string): string {
  return `${ownerUid}_${date}_${startTime.replace(':', '')}`;
}

function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function timeOfMinutes(total: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/**
 * Null when a free window is fine, else why not. A window is at least one
 * spot long and at most COMP_EVAL_WINDOW_MAX_MINUTES.
 */
export function compEvalWindowProblem(slot: { date: unknown; startTime: unknown; endTime: unknown }, todayKey: string): string | null {
  if (!isCompEvalDateKey(slot.date)) return 'bad_date';
  if (!isCompEvalTime(slot.startTime) || !isCompEvalTime(slot.endTime)) return 'bad_time';
  const length = minutesOf(slot.endTime) - minutesOf(slot.startTime);
  if (length < COMP_EVAL_SLOT_MINUTES || length > COMP_EVAL_WINDOW_MAX_MINUTES) return 'bad_time';
  if (isCompEvalDateKey(todayKey) && slot.date < todayKey) return 'past_date';
  return null;
}

/** Null when one SPOT is fine (exactly 10 minutes), else why not. The lane's check. */
export function compEvalSlotProblem(slot: { date: unknown; startTime: unknown; endTime: unknown }, todayKey: string): string | null {
  const problem = compEvalWindowProblem(slot, todayKey);
  if (problem) return problem;
  const length = minutesOf(String(slot.endTime)) - minutesOf(String(slot.startTime));
  // On the 10-minute grid too, so one person's spots can never overlap
  // (5:00 and 5:05 would be two spots sharing five minutes).
  if (length !== COMP_EVAL_SLOT_MINUTES || minutesOf(String(slot.startTime)) % COMP_EVAL_SLOT_MINUTES !== 0) return 'bad_time';
  return null;
}

/**
 * Cut one free window into back-to-back 10-minute spots ("free 4 to 5" is
 * six spots), on the 10-minute grid: a window starting 4:05 starts its first
 * spot at 4:10. A short last piece is dropped rather than offered as a half
 * spot.
 */
export function splitCompEvalWindow(slot: CompEvalSlotTime): CompEvalSlotTime[] {
  if (!isCompEvalTime(slot.startTime) || !isCompEvalTime(slot.endTime)) return [];
  const start = Math.ceil(minutesOf(slot.startTime) / COMP_EVAL_SLOT_MINUTES) * COMP_EVAL_SLOT_MINUTES;
  const end = minutesOf(slot.endTime);
  const out: CompEvalSlotTime[] = [];
  for (let at = start; at + COMP_EVAL_SLOT_MINUTES <= end; at += COMP_EVAL_SLOT_MINUTES) {
    out.push({ date: slot.date, startTime: timeOfMinutes(at), endTime: timeOfMinutes(at + COMP_EVAL_SLOT_MINUTES) });
  }
  return out;
}

/** The most spots one set date can cover (a whole 12-hour window). */
export const COMP_EVAL_COVER_MAX = 72;

/**
 * Every 10-minute spot id of `ownerUid` that a date from `startTime` to
 * `endTime` touches (no end: one spot's length). An off-grid start still
 * covers the spot it falls inside: 5:35 to 5:45 touches 5:30 and 5:40.
 */
export function compEvalCoveredSlotIds(ownerUid: string, date: string, startTime: string, endTime: string): string[] {
  if (!ownerUid || !isCompEvalDateKey(date) || !isCompEvalTime(startTime)) return [];
  const start = minutesOf(startTime);
  const end = isCompEvalTime(endTime) && minutesOf(endTime) > start ? minutesOf(endTime) : start + COMP_EVAL_SLOT_MINUTES;
  const ids: string[] = [];
  for (let at = Math.floor(start / COMP_EVAL_SLOT_MINUTES) * COMP_EVAL_SLOT_MINUTES; at < end && at < 24 * 60; at += COMP_EVAL_SLOT_MINUTES) {
    ids.push(compEvalSlotId(ownerUid, date, timeOfMinutes(at)));
    if (ids.length >= COMP_EVAL_COVER_MAX) break;
  }
  return ids;
}

/** One spot starting at `startTime` (rounded up onto the 10-minute grid): its end ('' past midnight). */
export function compEvalSpotEnd(startTime: string): string {
  if (!isCompEvalTime(startTime)) return '';
  const end = Math.ceil(minutesOf(startTime) / COMP_EVAL_SLOT_MINUTES) * COMP_EVAL_SLOT_MINUTES + COMP_EVAL_SLOT_MINUTES;
  return end >= 24 * 60 ? '' : timeOfMinutes(end);
}

/** Read a compEvalSlots doc defensively. */
export function normalizeCompEvalSlot(slotId: string, raw: unknown): CompEvalSlot | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const date = dateOr(data.date);
  const startTime = timeOr(data.startTime);
  const endTime = timeOr(data.endTime);
  if (!date || !startTime || !endTime || endTime <= startTime) return null;
  const heldByOfferingId = str(data.heldByOfferingId, 200);
  const status: CompEvalSlotStatus = !heldByOfferingId ? 'open' : data.status === 'booked' ? 'booked' : 'asked';
  return {
    slotId,
    ownerUid: str(data.ownerUid, 128),
    ownerName: str(data.ownerName, COMP_EVAL_NAME_MAX),
    date,
    startTime,
    endTime,
    status,
    heldByOfferingId,
    heldByClassName: heldByOfferingId ? str(data.heldByClassName, COMP_EVAL_NAME_MAX) : '',
    createdAtMs: num(data.createdAtMs),
  };
}

/** Earliest first: day, then start, then whose. */
export function sortCompEvalSlots<T extends CompEvalSlotTime & { ownerName?: string }>(slots: readonly T[]): T[] {
  return [...slots].sort((a, b) =>
    a.date.localeCompare(b.date)
    || a.startTime.localeCompare(b.startTime)
    || (a.ownerName ?? '').localeCompare(b.ownerName ?? ''));
}

/** The studio's day (America/Chicago) for a moment, 'YYYY-MM-DD'. */
export function compEvalStudioDayKey(nowMs: number): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(nowMs));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Minutes since midnight in the studio's time zone, for "earlier today". */
export function compEvalStudioNowMinutes(nowMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(nowMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get('hour') * 60 + get('minute');
}

/** True when a spot has already started (a past day, or earlier today), studio time. */
export function compEvalSpotStarted(slot: { date: string; startTime: string }, nowMs: number): boolean {
  const today = compEvalStudioDayKey(nowMs);
  if (slot.date !== today) return slot.date < today;
  return isCompEvalTime(slot.startTime) && minutesOf(slot.startTime) <= compEvalStudioNowMinutes(nowMs);
}

const MONTH_WORDS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
  jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

function dayKeyOf(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A day with no year: this year, or next year when that day is more than
 * about four months behind us ("1/5" typed in November is January). A day
 * only just gone stays this year, so it is caught as already passed rather
 * than quietly moved a whole year out.
 */
function inferYear(month: number, day: number, todayKey: string): string {
  const year = Number(todayKey.slice(0, 4)) || new Date().getFullYear();
  const key = dayKeyOf(year, month, day);
  if (!isCompEvalDateKey(todayKey) || !isCompEvalDateKey(key) || key >= todayKey) return key;
  const behindDays = (keyToUtc(todayKey) - keyToUtc(key)) / 86_400_000;
  return behindDays > 120 ? dayKeyOf(year + 1, month, day) : key;
}

function parseDayPart(text: string, todayKey: string): { date: string; rest: string } | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(text);
  if (iso) return { date: dayKeyOf(Number(iso[1]), Number(iso[2]), Number(iso[3])), rest: text.slice(iso[0].length) };
  const slash = /^(\d{1,2})[/.](\d{1,2})(?:[/.](\d{2,4}))?\b/.exec(text);
  if (slash) {
    const month = Number(slash[1]);
    const day = Number(slash[2]);
    const rawYear = slash[3];
    const date = rawYear
      ? dayKeyOf(rawYear.length === 2 ? 2000 + Number(rawYear) : Number(rawYear), month, day)
      : inferYear(month, day, todayKey);
    return { date, rest: text.slice(slash[0].length) };
  }
  const word = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b/.exec(text);
  if (word && MONTH_WORDS[word[1]]) {
    const month = MONTH_WORDS[word[1]];
    const day = Number(word[2]);
    const date = word[3] ? dayKeyOf(Number(word[3]), month, day) : inferYear(month, day, todayKey);
    return { date, rest: text.slice(word[0].length) };
  }
  return null;
}

interface ClockPiece { hour: number; minute: number; meridiem: '' | 'am' | 'pm' }

function parseClock(text: string): ClockPiece | null {
  if (/^noon$/i.test(text.trim())) return { hour: 12, minute: 0, meridiem: 'pm' };
  const match = /^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?|a|p)?$/.exec(text.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  if (hour > 23 || minute > 59) return null;
  const mer = match[3] ? (match[3].startsWith('a') ? 'am' : 'pm') : '';
  if (mer && (hour < 1 || hour > 12)) return null;
  return { hour, minute, meridiem: mer };
}

function hourOf(piece: ClockPiece, meridiem: '' | 'am' | 'pm'): number {
  if (meridiem === 'am') return piece.hour === 12 ? 0 : piece.hour;
  if (meridiem === 'pm') return piece.hour === 12 ? 12 : piece.hour + 12;
  // Bare: a "5:30" at a dance studio is the evening; "9" or "10" is a
  // Saturday morning; 12 is noon; 13 to 23 is already a 24-hour clock.
  return piece.hour >= 1 && piece.hour <= 7 ? piece.hour + 12 : piece.hour;
}

function clockText(hour: number, minute: number): string | null {
  if (hour < 0 || hour > 23) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function to24(piece: ClockPiece, meridiem: '' | 'am' | 'pm'): string | null {
  return clockText(hourOf(piece, meridiem), piece.minute);
}

/**
 * A start and an end as typed to a 24-hour pair. The end may leave out its
 * am/pm ("10am-12", "5:30pm-6:15"): it is the first reading after the start.
 * The start may leave it out too ("4-6pm"): it borrows the end's, unless that
 * puts it after the end ("11-1pm" is 11am to 1pm).
 */
function resolveRange(start: ClockPiece, end: ClockPiece): { startTime: string; endTime: string } | null {
  const total = (hour: number, minute: number) => hour * 60 + minute;
  let startHour: number;
  if (start.meridiem) startHour = hourOf(start, start.meridiem);
  else if (end.meridiem) {
    const endHour = hourOf(end, end.meridiem);
    startHour = hourOf(start, end.meridiem);
    if (total(startHour, start.minute) >= total(endHour, end.minute)) {
      startHour = hourOf(start, end.meridiem === 'pm' ? 'am' : 'pm');
    }
  } else startHour = hourOf(start, '');
  const startAt = total(startHour, start.minute);
  let endHour: number;
  if (end.meridiem) endHour = hourOf(end, end.meridiem);
  else {
    const options = end.hour >= 13 || end.hour === 0 ? [end.hour] : [end.hour === 12 ? 12 : end.hour, end.hour === 12 ? 24 : end.hour + 12];
    endHour = options.find((hour) => total(hour, end.minute) > startAt) ?? options[0];
  }
  const startTime = clockText(startHour, start.minute);
  const endTime = clockText(endHour, end.minute);
  return startTime && endTime ? { startTime, endTime } : null;
}

export interface CompEvalScheduleParse {
  /** The free windows as read, one per line. */
  windows: CompEvalSlotTime[];
  /** Those windows cut into 10-minute spots, duplicates dropped, earliest first. */
  slots: CompEvalSlotTime[];
  /** Lines that could not be read, with the line as typed. */
  problems: { line: string; reason: string }[];
}

/**
 * Read a pasted schedule, one time per line: "11/10 5:30pm-6:15pm",
 * "Tue Nov 10 4 to 6pm", "2026-11-10 17:30-18:15". A weekday word up front is
 * ignored (the date decides). A day with no year is this year, or next year
 * once it is well behind us. A bare hour 1 to 7 with no am/pm is the
 * evening, 8 to 11 the morning, 12 noon.
 */
export function parseCompEvalSchedule(text: string, todayKey: string): CompEvalScheduleParse {
  const windows: CompEvalSlotTime[] = [];
  const problems: { line: string; reason: string }[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || isCompEvalHeaderLine(line)) continue;
    let rest = line.toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ');
    rest = rest.replace(/^(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)[a-z]*\.?,?\s+/, '');
    const day = parseDayPart(rest, todayKey);
    if (!day || !isCompEvalDateKey(day.date)) {
      problems.push({ line, reason: 'No date I can read. Start the line with a date like 11/10.' });
      continue;
    }
    const times = day.rest.replace(/^[,\s@]+/, '').replace(/^at\s+/, '').trim();
    const split = /^(.+?)\s*(?:-|to|until|till)\s*(.+)$/.exec(times);
    if (!split) {
      // Just a start: one 10-minute spot.
      const only = parseClock(times);
      const startTime = only ? to24(only, only.meridiem) : null;
      const endTime = startTime ? compEvalSpotEnd(startTime) : '';
      if (!startTime || !endTime) {
        problems.push({ line, reason: 'Needs a time, like 5:30pm, or a start and an end, like 5:30pm-6:15pm.' });
        continue;
      }
      const problem = compEvalWindowProblem({ date: day.date, startTime, endTime }, todayKey);
      if (problem) {
        problems.push({ line, reason: problem === 'past_date' ? 'That day has already passed.' : 'Could not read that time.' });
        continue;
      }
      windows.push({ date: day.date, startTime, endTime });
      continue;
    }
    const startPiece = parseClock(split[1]);
    const endPiece = parseClock(split[2]);
    if (!startPiece || !endPiece) {
      problems.push({ line, reason: 'Could not read the times. Try 5:30pm-6:15pm.' });
      continue;
    }
    const range = resolveRange(startPiece, endPiece);
    const slot = { date: day.date, startTime: range?.startTime ?? '', endTime: range?.endTime ?? '' };
    const problem = compEvalWindowProblem(slot, todayKey);
    if (problem) {
      problems.push({
        line,
        reason: problem === 'past_date'
          ? 'That day has already passed.'
          : 'Check the times: the end has to come after the start (add am or pm if it helps), and one line can cover at most 12 hours.',
      });
      continue;
    }
    windows.push(slot);
  }
  return { windows, slots: compEvalSpotsOf(windows), problems };
}

/** Windows to unique 10-minute spots, earliest first. */
export function compEvalSpotsOf(windows: readonly CompEvalSlotTime[]): CompEvalSlotTime[] {
  const seen = new Map<string, CompEvalSlotTime>();
  for (const window of windows) {
    for (const spot of splitCompEvalWindow(window)) seen.set(`${spot.date} ${spot.startTime}`, spot);
  }
  return sortCompEvalSlots([...seen.values()]);
}

/** An Excel day serial (1900 system) to 'YYYY-MM-DD'. */
function excelSerialDay(serial: number): string {
  const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86_400_000;
  const d = new Date(ms);
  return dayKeyOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** An Excel time fraction (0.5 is noon) to 'h:mmam'. */
function excelFractionClock(fraction: number): string {
  const total = Math.round(fraction * 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

/**
 * A spreadsheet's rows to schedule lines parseCompEvalSchedule can read. A
 * row is its cells in order; Excel's numbers are turned back into words (a
 * whole number in the date range is a day, a fraction is a time, both is a
 * day and a time). A header row ("Date, Start, End") simply fails to read and
 * is skipped quietly. Two times in a row are a start and an end.
 */
export function compEvalSheetRowsToLines(rows: readonly (readonly string[])[]): string[] {
  const lines: string[] = [];
  // A clock in a text cell: needs a colon or an am/pm ("5:30", "5pm",
  // "4:00 PM"). A bare "1" is a row number, never a time.
  const looksLikeClock = (cell: string) => /[:]|[ap]\.?m?\.?$|^noon$/i.test(cell) && !!parseClock(cell.toLowerCase());
  for (const row of rows) {
    let day = '';
    const times: string[] = [];
    const words: string[] = [];
    for (const rawCell of row) {
      const cell = String(rawCell ?? '').trim();
      if (!cell) continue;
      if (/^\d+(\.\d+)?$/.test(cell)) {
        const value = Number(cell);
        if (value >= 30000 && value < 80000) {
          if (!day) day = excelSerialDay(value);
          const fraction = value - Math.floor(value);
          if (fraction > 0) times.push(excelFractionClock(fraction));
          continue;
        }
        if (value > 0 && value < 1) {
          times.push(excelFractionClock(value));
          continue;
        }
        continue; // a row number, a count: not part of the time
      }
      if (looksLikeClock(cell)) {
        times.push(cell.toLowerCase());
        continue;
      }
      // "4pm-6pm" in one cell is the whole range.
      if (/\d.*(-|to)\s*\d/i.test(cell) && !/^\d{1,2}[/.]\d{1,2}([/.]\d{2,4})?$/.test(cell) && !/^\d{4}-\d{1,2}-\d{1,2}$/.test(cell)) {
        times.push(cell.toLowerCase());
        continue;
      }
      if (!day && parseDayPart(cell.toLowerCase().replace(/^(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)[a-z]*\.?,?\s+/, ''), '2000-01-01')) {
        day = cell;
        continue;
      }
      words.push(cell); // a header word, a room, a note: never part of the time
    }
    if (!day) {
      if (words.length) lines.push(words.join(' ')); // a header row, skipped by the reader, or a line it reports
      continue;
    }
    const timePart = times.length >= 2 ? `${times[0]}-${times[1]}` : times[0] ?? '';
    lines.push(`${day} ${timePart}`.trim());
  }
  return lines;
}

/** Lines of a schedule that are only a header ("Date Start End") are not problems. */
export function isCompEvalHeaderLine(line: string): boolean {
  return /^(date|day|when)\b/i.test(line.trim()) && !/\d/.test(line);
}
